import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { Check, ArrowRight, X, Crown, Loader2, Gift, Sparkles, Timer } from "lucide-react";

const PLAN_THEME = {
    starter: { ring: "border-[var(--border-bright)]", accent: "text-[var(--text)]" },
    pro: { ring: "border-[var(--gold)]", accent: "text-[var(--gold-bright)]" },
    elite: { ring: "border-[var(--gold-deep)]", accent: "text-[var(--gold)]" },
};

export default function PricingPage() {
    const [plans, setPlans] = useState([]);
    const [params] = useSearchParams();
    const canceled = params.get("canceled") === "1";
    const [openPlan, setOpenPlan] = useState(null);
    const [promoOpen, setPromoOpen] = useState(false);
    const { user } = useAuth();

    useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)); }, []);

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="pricing-page">
            <header className="border-b border-[var(--border)]">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="block"><Logo size="sm" /></Link>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setPromoOpen(true)} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="open-promo">
                            <Gift size={12} /> Have a promo code?
                        </button>
                        {!user && <Link to="/login" className="btn-ghost text-xs" data-testid="pricing-login-link">Sign in</Link>}
                    </div>
                </div>
            </header>

            <section className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
                <div className="max-w-3xl">
                    <p className="label-tiny mb-4">/ Coaching with King Seidorb</p>
                    <h1 className="font-serif text-5xl md:text-7xl mb-6 leading-[0.95]">
                        The body you've been chasing isn't <span className="gold-gradient-text">further away</span>.<br />It's behind a different program.
                    </h1>
                    <p className="text-base md:text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed">
                        Three coaching tiers. Start with a 7-day free trial, redeem a promo code, or pay monthly through PayPal.
                    </p>
                </div>
                {canceled && (
                    <div className="mt-8 max-w-xl border border-[var(--gold-deep)] bg-[var(--surface)] p-4 text-sm" data-testid="pricing-canceled-banner">
                        Checkout was canceled — pick again whenever you're ready.
                    </div>
                )}
            </section>

            <div className="gold-rule max-w-6xl mx-auto" />

            <section className="max-w-6xl mx-auto px-6 py-16 lg:py-20" data-testid="plans-section">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--border)]">
                    {plans.map((p) => {
                        const isPopular = p.id === "pro";
                        const theme = PLAN_THEME[p.id] || PLAN_THEME.starter;
                        return (
                            <div key={p.id} className={`relative bg-[var(--surface)] border-t-2 ${theme.ring} p-8 flex flex-col`} data-testid={`plan-card-${p.id}`}>
                                {isPopular && (
                                    <div className="absolute -top-px right-6 bg-[var(--gold)] text-[#0a0908] px-3 py-1 text-[10px] font-mono tracking-[0.25em] uppercase">
                                        Most chosen
                                    </div>
                                )}
                                {p.id === "elite" && <Crown size={18} className="text-[var(--gold-bright)] mb-4" />}
                                <p className={`label-tiny mb-2 ${theme.accent}`}>/ {p.tagline}</p>
                                <h2 className="font-serif text-5xl mb-2">{p.name}</h2>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="font-display text-6xl">${Math.floor(p.amount)}</span>
                                    <span className="font-display text-3xl text-[var(--text-muted)]">.{String(p.amount).split(".")[1] || "00"}</span>
                                </div>
                                <p className="text-xs text-[var(--text-muted)] font-mono mb-6">/ {p.interval} · PayPal</p>

                                <ul className="space-y-3 mb-8 flex-1" data-testid={`plan-features-${p.id}`}>
                                    {p.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm">
                                            <Check size={14} className="text-[var(--gold-bright)] mt-1 flex-shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={() => setOpenPlan(p)}
                                    className={`w-full inline-flex items-center justify-center gap-2 py-4 transition-colors ${
                                        isPopular ? "bg-[var(--gold)] text-[#0a0908] hover:bg-[var(--gold-bright)]"
                                            : "border border-[var(--border-bright)] hover:border-[var(--gold)] hover:text-[var(--gold-bright)]"
                                    } font-display tracking-[0.2em] uppercase text-sm`}
                                    data-testid={`plan-cta-${p.id}`}
                                >
                                    Start 7-day trial <ArrowRight size={14} />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <p className="text-center text-xs text-[var(--text-muted)] font-mono mt-10">
                    7-day free trial · no card required · PayPal billing after trial
                </p>
            </section>

            <section className="max-w-3xl mx-auto px-6 py-16 border-t border-[var(--border)]">
                <p className="label-tiny mb-3">/ Frequently asked</p>
                <h2 className="font-serif text-4xl mb-8">Honest answers.</h2>
                <div className="space-y-6 text-sm">
                    <div>
                        <p className="font-medium mb-1">How does the free trial work?</p>
                        <p className="text-[var(--text-muted)]">You get 7 days of full access — no card, no charge. On day 7 we'll invoice you through PayPal. Don't want to continue? Walk away, no charge.</p>
                    </div>
                    <div>
                        <p className="font-medium mb-1">Can I cancel anytime?</p>
                        <p className="text-[var(--text-muted)]">Yes. Cancel any PayPal subscription directly in your PayPal account, or message King in-app.</p>
                    </div>
                    <div>
                        <p className="font-medium mb-1">How do promo codes work?</p>
                        <p className="text-[var(--text-muted)]">Click "Have a promo code?" at the top, enter your code, and access is applied to your account instantly.</p>
                    </div>
                    <div>
                        <p className="font-medium mb-1">How fast do I get my program?</p>
                        <p className="text-[var(--text-muted)]">Within 48 hours of payment + intake form. King personally reviews every Pro and Elite intake.</p>
                    </div>
                </div>
            </section>

            <TrialModal plan={openPlan} onClose={() => setOpenPlan(null)} user={user} />
            <PromoModal open={promoOpen} onClose={() => setPromoOpen(false)} user={user} />
        </div>
    );
}

function TrialModal({ plan, onClose, user }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const nav = useNavigate();

    useEffect(() => { if (plan) { setErr(""); setBusy(false); setDone(null); } }, [plan]);
    if (!plan) return null;

    const startTrial = async () => {
        if (!user) {
            nav(`/register?trial=${plan.id}&next=/pricing`);
            return;
        }
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/pricing/trial", { plan_id: plan.id });
            setDone(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="trial-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold-deep)] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div>
                        <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ 7-day free trial</p>
                        <h3 className="font-serif text-3xl">{plan.name} — ${plan.amount}/{plan.interval}</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    {done ? (
                        <>
                            <div className="flex items-center gap-3 text-[var(--gold-bright)]">
                                <Sparkles size={18} />
                                <p className="font-serif text-xl">You're in.</p>
                            </div>
                            <p className="text-sm text-[var(--text-muted)]">Your 7-day {plan.name} trial ends {new Date(done.trial_ends_at).toLocaleDateString()}. King will message you to set up PayPal billing before it ends.</p>
                            <button onClick={() => nav("/")} className="btn-primary w-full" data-testid="trial-go">Enter the dashboard</button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-[var(--text-muted)]">
                                <Timer size={14} className="inline mr-1 text-[var(--gold)]" />
                                7 days of full {plan.name} access. No card, no charge today.
                                After day 7, King will send a PayPal invoice to continue.
                            </p>
                            {!user && (
                                <p className="text-xs text-[var(--gold-bright)] font-mono">You'll be sent to register first (30 seconds).</p>
                            )}
                            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                            <button onClick={startTrial} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="start-trial">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <>Start free trial <ArrowRight size={14} /></>}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function PromoModal({ open, onClose, user }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const nav = useNavigate();

    if (!open) return null;

    const redeem = async () => {
        if (!user) { nav(`/register?promo=${encodeURIComponent(code)}&next=/pricing`); return; }
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/promo/redeem", { code });
            setDone(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="promo-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold)] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div>
                        <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ Promo code</p>
                        <h3 className="font-serif text-3xl">Redeem</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    {done ? (
                        <>
                            <div className="flex items-center gap-3 text-[var(--gold-bright)]">
                                <Sparkles size={18} />
                                <p className="font-serif text-xl">Redeemed.</p>
                            </div>
                            <p className="text-sm text-[var(--text-muted)]">{done.description}. Access granted until {new Date(done.granted_until).toLocaleDateString()}.</p>
                            <button onClick={() => { onClose(); nav("/"); }} className="btn-primary w-full" data-testid="promo-go">Open dashboard</button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-[var(--text-muted)]">Enter the promo code King shared with you.</p>
                            <input
                                autoFocus
                                className="input-dark font-mono text-lg tracking-[0.3em] uppercase"
                                placeholder="NDEFREE"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                data-testid="promo-code-input"
                            />
                            {!user && <p className="text-xs text-[var(--gold-bright)] font-mono">Not signed in? You'll be sent to register with the code applied.</p>}
                            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                            <button onClick={redeem} disabled={busy || code.trim().length < 2} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="redeem-btn">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Redeeming…</> : <><Gift size={14} /> Redeem code</>}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
